<template>
	<Teleport to="body">
		<div class="document-conflict-overlay">
			<section
				ref="dialogRef"
				class="document-conflict"
				role="alertdialog"
				aria-modal="true"
				:aria-labelledby="titleId"
				:aria-describedby="descriptionId"
				tabindex="-1"
				@keydown.tab="trapFocus"
			>
				<h3 :id="titleId">This {{ documentLabel }} was changed on another device</h3>
				<p :id="descriptionId">
					It was saved somewhere else while it was open here, so what you changed here in the last moments is not
					saved yet. Choose which version to keep.
				</p>
				<div class="document-conflict__choices">
					<button type="button" class="document-conflict__choice" :disabled="busy" @click="$emit('load-latest')">
						<strong>Load the latest version</strong>
						<span>Shows what was saved on the other device. Your recent changes here are dropped.</span>
					</button>
					<button type="button" class="document-conflict__choice" :disabled="busy" @click="$emit('keep-mine')">
						<strong>Keep my version</strong>
						<span>Saves what you see here, replacing the changes made on the other device.</span>
					</button>
				</div>
				<p v-if="error" class="document-conflict__error" role="alert">{{ error }}</p>
			</section>
		</div>
	</Teleport>
</template>

<script setup>
import { getCurrentInstance, nextTick, onMounted, onUnmounted, ref } from 'vue';

defineProps({
	// The kind of document, in lower case: "proposal", "quotation", "assignment confirmation".
	documentLabel: { type: String, required: true },
	busy: { type: Boolean, default: false },
	error: { type: String, default: '' },
});

defineEmits(['load-latest', 'keep-mine']);

const instanceId = getCurrentInstance()?.uid ?? Math.random().toString(36).slice(2);
const titleId = `document-conflict-${instanceId}-title`;
const descriptionId = `document-conflict-${instanceId}-description`;
const dialogRef = ref(null);

const trapFocus = (event) => {
	const focusable = Array.from(dialogRef.value?.querySelectorAll('button:not(:disabled)') || []);
	if (!focusable.length) {
		event.preventDefault();
		return;
	}
	const first = focusable[0];
	const last = focusable[focusable.length - 1];
	if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.value)) {
		event.preventDefault();
		last.focus();
	} else if (!event.shiftKey && document.activeElement === last) {
		event.preventDefault();
		first.focus();
	}
};

// The page behind stays still and out of reach until a version is chosen.
let bodyOverflow = null;
let appRoot = null;
let appRootWasInert = false;

onMounted(() => {
	bodyOverflow = document.body.style.overflow;
	document.body.style.overflow = 'hidden';
	appRoot = document.getElementById('app');
	if (appRoot && !appRoot.contains(dialogRef.value)) {
		appRootWasInert = Boolean(appRoot.inert);
		appRoot.inert = true;
	} else {
		appRoot = null;
	}
	nextTick(() => dialogRef.value?.focus?.({ preventScroll: true }));
});

onUnmounted(() => {
	document.body.style.overflow = bodyOverflow ?? '';
	if (appRoot) appRoot.inert = appRootWasInert;
});
</script>

<style scoped>
.document-conflict-overlay {
	position: fixed;
	z-index: 1500;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 16px;
	background: rgba(15, 23, 42, 0.46);
	box-sizing: border-box;
}

.document-conflict {
	display: flex;
	flex-direction: column;
	gap: 14px;
	width: min(520px, calc(100vw - 32px));
	padding: 22px;
	border: 1px solid var(--border-color, #e5e7eb);
	border-radius: 12px;
	outline: none;
	background: var(--noc-background, #fff);
	box-shadow: 0 22px 60px rgba(15, 23, 42, 0.24);
	color: var(--text-color, #333);
}

.document-conflict h3 {
	margin: 0;
	font-size: 1.15rem;
	font-weight: 650;
	line-height: 1.35;
}

.document-conflict p {
	margin: 0;
	color: var(--text-color-secondary, #6b7280);
	font-size: 0.87rem;
	line-height: 1.45;
}

.document-conflict__choices {
	display: grid;
	gap: 8px;
}

.document-conflict__choice {
	display: flex;
	flex-direction: column;
	gap: 3px;
	padding: 12px 14px;
	border: 1px solid var(--border-color, #d1d5db);
	border-radius: 8px;
	background: var(--noc-background, #fff);
	color: var(--text-color, #333);
	font: inherit;
	text-align: left;
	cursor: pointer;
	touch-action: manipulation;
}

.document-conflict__choice strong {
	font-size: 0.9rem;
	font-weight: 650;
}

.document-conflict__choice span {
	color: var(--text-color-secondary, #6b7280);
	font-size: 0.82rem;
	line-height: 1.4;
}

.document-conflict__choice:focus-visible {
	border-color: var(--noc-blue, #1261c9);
	outline: none;
}

.document-conflict__choice:disabled {
	opacity: 0.6;
	cursor: wait;
}

.document-conflict__error {
	color: #b42318 !important;
}

@media (hover: hover) {
	.document-conflict__choice:hover:not(:disabled) {
		border-color: var(--noc-blue, #1261c9);
		background: var(--noc-background-secondary, #f5f6f8);
	}
}
</style>
